// ==================== CANCELLATION POLICY PAGE COMPONENT ====================

import React, { useEffect, useState } from "react";
import Footer from "./Footer.js";
import Headerpage from "./Header2.js";
import { getData, serverURL } from "../../../Services/Fetchnodeservices.js";
import {
  Shield,
  Lock,
  Clock,
  CreditCard,
  Wallet,
  Phone,
  Mail,
  AlertCircle,
  CheckCircle,
  FileText,
  Calendar,
  HelpCircle,
  MessageCircle,
} from "lucide-react";

const CancellationPolicy = ({ isDark }) => {
  const [policyData, setPolicyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Theme colors
  const t = isDark
    ? {
        bg: "#0a0c10",
        cardBg: "#141824",
        text: "#e2e8f0",
        textSecondary: "#94a3b8",
        border: "rgba(255,255,255,0.08)",
        heading: "#ffffff",
        code: "#1e293b",
        accent: "#ef4444",
        accentLight: "#f87171",
      }
    : {
        bg: "#f8fafc",
        cardBg: "#ffffff",
        text: "#334155",
        textSecondary: "#64748b",
        border: "rgba(0,0,0,0.1)",
        heading: "#0f172a",
        code: "#f1f5f9",
        accent: "#ef4444",
        accentLight: "#fca5a5",
      };

  // Fetch cancellation policy from backend
  const fetchPolicy = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getData("cancellationpolicy/cancellationpolicy");
      console.log("API Response:", response);

      if (response?.status && response?.data) {
        setPolicyData(response.data);
      } else {
        setError("Cancellation policy not found");
      }
    } catch (err) {
      console.error("Error fetching cancellation policy:", err);
      setError("Failed to load cancellation policy. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPolicy();
  }, []);

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div
        style={{
          fontFamily: "'Poppins', sans-serif",
          background: t.bg,
          minHeight: "100vh",
        }}
      >
        <Headerpage />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: "60px",
                height: "60px",
                border: `3px solid ${t.border}`,
                borderTopColor: t.accent,
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 20px",
              }}
            />
            <p style={{ color: t.textSecondary }}>Loading cancellation policy...</p>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          fontFamily: "'Poppins', sans-serif",
          background: t.bg,
          minHeight: "100vh",
        }}
      >
        <Headerpage />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            padding: "20px",
          }}
        >
          <div
            style={{
              textAlign: "center",
              backgroundColor: t.cardBg,
              padding: "40px",
              borderRadius: "20px",
              border: `1px solid ${t.border}`,
              maxWidth: "500px",
            }}
          >
            <div
              style={{
                width: "80px",
                height: "80px",
                background: "#fee2e2",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
              }}
            >
              <AlertCircle size={40} color="#ef4444" />
            </div>
            <h2 style={{ color: t.heading, marginBottom: "12px" }}>
              Cancellation Policy Not Found
            </h2>
            <p style={{ color: t.textSecondary, marginBottom: "24px" }}>
              {error}
            </p>
            <button
              onClick={fetchPolicy}
              style={{
                padding: "12px 24px",
                backgroundColor: t.accent,
                color: "white",
                border: "none",
                borderRadius: "12px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              Try Again
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <style>{`
        .cancellation-content {
          word-wrap: break-word;
        }
        .cancellation-content h1 {
          font-size: 2rem;
          margin-top: 1.5em;
          margin-bottom: 0.5em;
          color: ${t.heading};
          font-weight: 700;
        }
        .cancellation-content h2 {
          font-size: 1.5rem;
          margin-top: 1.5em;
          margin-bottom: 0.5em;
          color: ${t.heading};
          font-weight: 600;
          border-left: 4px solid ${t.accent};
          padding-left: 1rem;
        }
        .cancellation-content h3 {
          font-size: 1.25rem;
          margin-top: 1.2em;
          margin-bottom: 0.5em;
          color: ${t.heading};
          font-weight: 600;
        }
        .cancellation-content p {
          line-height: 1.8;
          margin-bottom: 1rem;
          color: ${t.text};
        }
        .cancellation-content ul,
        .cancellation-content ol {
          margin: 1rem 0;
          padding-left: 2rem;
          color: ${t.text};
        }
        .cancellation-content li {
          margin: 0.5rem 0;
          line-height: 1.7;
        }
        .cancellation-content hr {
          margin: 2rem 0;
          border: none;
          height: 1px;
          background: ${t.border};
        }
        .cancellation-content a {
          color: ${t.accent};
          text-decoration: none;
        }
        .cancellation-content a:hover {
          text-decoration: underline;
        }
        .cancellation-content strong {
          color: ${t.heading};
          font-weight: 600;
        }
        @media (max-width: 768px) {
          .cancellation-content h1 {
            font-size: 1.5rem;
          }
          .cancellation-content h2 {
            font-size: 1.25rem;
          }
        }
      `}</style>

      <div
        style={{
          fontFamily: "'Poppins', sans-serif",
          background: t.bg,
          minHeight: "100vh",
        }}
      >
        <Headerpage />

        {/* Hero Section with Thumbnail Background */}
        <section
          style={{
            position: "relative",
            minHeight: "400px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            color: "#fff",
            overflow: "hidden",
          }}
        >
          {/* Background Image */}
          {policyData?.page_thumbnail && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${serverURL}/images/${policyData.page_thumbnail})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            >
              {/* Dark Overlay */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "linear-gradient(135deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0.85) 100%)",
                }}
              />
            </div>
          )}

          {/* Fallback Gradient if no thumbnail */}
          {!policyData?.page_thumbnail && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "linear-gradient(135deg, #991b1b 0%, #dc2626 50%, #991b1b 100%)",
              }}
            />
          )}

          {/* Decorative Elements */}
          <div
            style={{
              position: "absolute",
              width: "300px",
              height: "300px",
              background: "rgba(239,68,68,0.15)",
              filter: "blur(60px)",
              borderRadius: "50%",
              top: "-150px",
              left: "-100px",
            }}
          />
          <div
            style={{
              position: "absolute",
              width: "250px",
              height: "250px",
              background: "rgba(239,68,68,0.1)",
              filter: "blur(50px)",
              borderRadius: "50%",
              bottom: "-100px",
              right: "-80px",
            }}
          />

          {/* Content */}
          <div
            style={{
              position: "relative",
              zIndex: 2,
              maxWidth: "800px",
              padding: "60px 20px",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "10px",
                background: "rgba(255,255,255,0.15)",
                backdropFilter: "blur(10px)",
                padding: "8px 20px",
                borderRadius: "40px",
                marginBottom: "24px",
              }}
            >
              <Clock size={18} />
              <span style={{ fontSize: "14px" }}>Cancellation Policy</span>
            </div>

            <h1
              style={{
                fontSize: "clamp(36px, 6vw, 56px)",
                fontWeight: "700",
                marginBottom: "20px",
                textShadow: "0 2px 10px rgba(0,0,0,0.3)",
              }}
            >
              {policyData?.page_title || "Cancellation Policy"}
            </h1>

            <p
              style={{
                fontSize: "16px",
                opacity: 0.9,
                marginBottom: "24px",
                maxWidth: "600px",
                margin: "0 auto",
              }}
            >
              We know plans can change - we've got your back!
            </p>

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
                flexWrap: "wrap",
                marginTop: "24px",
              }}
            >
              {[
                { icon: Clock, text: "Easy Cancellation" },
                { icon: CreditCard, text: "Secure Refunds" },
                { icon: CheckCircle, text: "No Hidden Fees" },
              ].map((item, index) => (
                <div
                  key={index}
                  style={{
                    background: "rgba(255,255,255,0.1)",
                    backdropFilter: "blur(10px)",
                    padding: "8px 16px",
                    borderRadius: "40px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "13px",
                  }}
                >
                  <item.icon size={16} />
                  <span>{item.text}</span>
                </div>
              ))}
            </div>

            {/* Last Updated */}
            <div
              style={{
                marginTop: "32px",
                fontSize: "13px",
                opacity: 0.7,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <Calendar size={14} />
              <span>Last Updated: {formatDate(policyData?.updated_at)}</span>
            </div>
          </div>
        </section>

        {/* Main Content Section */}
        <section
          style={{
            padding: "60px 20px",
            position: "relative",
            zIndex: 10,
          }}
        >
          <div
            style={{
              maxWidth: "1100px",
              margin: "0 auto",
            }}
          >
            {/* Key Highlights Cards */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "24px",
                marginBottom: "48px",
              }}
            >
              <div
                style={{
                  background: t.cardBg,
                  padding: "28px",
                  borderRadius: "20px",
                  border: `1px solid ${t.border}`,
                  textAlign: "center",
                  transition: "transform 0.3s, box-shadow 0.3s",
                }}
              >
                <div
                  style={{
                    width: "70px",
                    height: "70px",
                    background: `${t.accent}15`,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 20px",
                  }}
                >
                  <Clock size={32} color={t.accent} />
                </div>
                <h3 style={{ color: t.heading, fontSize: "18px", marginBottom: "12px" }}>
                  Free Cancellation
                </h3>
                <p style={{ color: t.textSecondary, fontSize: "14px", lineHeight: "1.6" }}>
                  Cancel your order free of charge before it ships
                </p>
              </div>

              <div
                style={{
                  background: t.cardBg,
                  padding: "28px",
                  borderRadius: "20px",
                  border: `1px solid ${t.border}`,
                  textAlign: "center",
                  transition: "transform 0.3s, box-shadow 0.3s",
                }}
              >
                <div
                  style={{
                    width: "70px",
                    height: "70px",
                    background: `${t.accent}15`,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 20px",
                  }}
                >
                  <CreditCard size={32} color={t.accent} />
                </div>
                <h3 style={{ color: t.heading, fontSize: "18px", marginBottom: "12px" }}>
                  Quick Refunds
                </h3>
                <p style={{ color: t.textSecondary, fontSize: "14px", lineHeight: "1.6" }}>
                  Refunds processed within 1-3 business days
                </p>
              </div>

              <div
                style={{
                  background: t.cardBg,
                  padding: "28px",
                  borderRadius: "20px",
                  border: `1px solid ${t.border}`,
                  textAlign: "center",
                  transition: "transform 0.3s, box-shadow 0.3s",
                }}
              >
                <div
                  style={{
                    width: "70px",
                    height: "70px",
                    background: `${t.accent}15`,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 20px",
                  }}
                >
                  <Wallet size={32} color={t.accent} />
                </div>
                <h3 style={{ color: t.heading, fontSize: "18px", marginBottom: "12px" }}>
                  Wallet Credit
                </h3>
                <p style={{ color: t.textSecondary, fontSize: "14px", lineHeight: "1.6" }}>
                  Instant credit to Zaptodoor wallet for faster checkout
                </p>
              </div>
            </div>

            {/* Main Policy Content Card */}
            <div
              style={{
                backgroundColor: t.cardBg,
                borderRadius: "28px",
                border: `1px solid ${t.border}`,
                overflow: "hidden",
                boxShadow: isDark
                  ? "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
                  : "0 25px 50px -12px rgba(0, 0, 0, 0.1)",
              }}
            >
              <div style={{ padding: "48px" }}>
                {/* Full HTML Content */}
                <div
                  className="cancellation-content"
                  dangerouslySetInnerHTML={{
                    __html: policyData?.page_description || "No content available",
                  }}
                />

                {/* Footer Info */}
                <div
                  style={{
                    marginTop: "48px",
                    paddingTop: "24px",
                    borderTop: `1px solid ${t.border}`,
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "16px",
                    fontSize: "13px",
                    color: t.textSecondary,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <FileText size={16} />
                    <span>Policy ID: {policyData?.id}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Calendar size={16} />
                    <span>Created: {formatDate(policyData?.created_at)}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <CheckCircle size={16} />
                    <span>Status: {policyData?.status === 1 ? "Active" : "Inactive"}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Need Help Section */}
            <div
              style={{
                background: `linear-gradient(135deg, ${t.accent}15, ${t.accent}08)`,
                borderRadius: "24px",
                padding: "40px",
                marginTop: "48px",
                textAlign: "center",
                border: `1px solid ${t.border}`,
              }}
            >
              <div
                style={{
                  width: "60px",
                  height: "60px",
                  background: `${t.accent}20`,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px",
                }}
              >
                <HelpCircle size={28} color={t.accent} />
              </div>
              <h3
                style={{
                  color: t.heading,
                  fontSize: "24px",
                  marginBottom: "12px",
                }}
              >
                Need Help Cancelling Your Order?
              </h3>
              <p
                style={{
                  color: t.textSecondary,
                  marginBottom: "28px",
                  fontSize: "15px",
                }}
              >
                Our support team is available 24/7 to assist you with cancellations and refunds
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                <a
                  href="tel:+919200018690"
                  style={{
                    background: t.accent,
                    color: "white",
                    padding: "12px 28px",
                    borderRadius: "40px",
                    textDecoration: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    fontWeight: "500",
                  }}
                >
                  <Phone size={18} />
                  Call Now: +91 9200018690
                </a>
                <a
                  href="mailto:info@zaptodoor.com"
                  style={{
                    background: "transparent",
                    color: t.text,
                    padding: "12px 28px",
                    borderRadius: "40px",
                    textDecoration: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    border: `1px solid ${t.border}`,
                    fontWeight: "500",
                  }}
                >
                  <Mail size={18} />
                  Email Support
                </a>
              </div>
            </div>

            {/* FAQ Preview */}
            <div
              style={{
                marginTop: "48px",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                gap: "20px",
              }}
            >
              <div
                style={{
                  background: t.cardBg,
                  padding: "24px",
                  borderRadius: "20px",
                  border: `1px solid ${t.border}`,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                  <MessageCircle size={22} color={t.accent} />
                  <h3 style={{ color: t.heading, fontSize: "16px", margin: 0 }}>Can I cancel after shipping?</h3>
                </div>
                <p style={{ color: t.textSecondary, fontSize: "14px", lineHeight: "1.6" }}>
                  If your order has already been shipped, you can still return the items according to our Return Policy.
                </p>
              </div>

              <div
                style={{
                  background: t.cardBg,
                  padding: "24px",
                  borderRadius: "20px",
                  border: `1px solid ${t.border}`,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                  <CreditCard size={22} color={t.accent} />
                  <h3 style={{ color: t.heading, fontSize: "16px", margin: 0 }}>How long for refund?</h3>
                </div>
                <p style={{ color: t.textSecondary, fontSize: "14px", lineHeight: "1.6" }}>
                  Refunds are processed within 1-3 business days after cancellation confirmation.
                </p>
              </div>

              <div
                style={{
                  background: t.cardBg,
                  padding: "24px",
                  borderRadius: "20px",
                  border: `1px solid ${t.border}`,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                  <Wallet size={22} color={t.accent} />
                  <h3 style={{ color: t.heading, fontSize: "16px", margin: 0 }}>Wallet refund?</h3>
                </div>
                <p style={{ color: t.textSecondary, fontSize: "14px", lineHeight: "1.6" }}>
                  Wallet payments are credited instantly to your Zaptodoor wallet.
                </p>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default CancellationPolicy;